import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { adminLogin } from "@/lib/adminApi";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await adminLogin(email.trim(), password);
      toast.success("Signed in");
      nav("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans grid place-items-center px-6">
      <div className="w-full max-w-sm" data-testid="admin-login-page">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Logo className="h-8 w-8" />
          <span className="font-heading text-base tracking-tight">
            chatepc<span className="text-epc-a">.co.uk</span>
          </span>
        </Link>
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground border border-border px-3 py-1 mb-6">
          <Lock className="h-3.5 w-3.5" /> Admin
        </div>
        <h1 className="font-heading text-3xl tracking-tight">Sign in to the dashboard</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Restricted area — authorised users only.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input
              id="email"
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="rounded-none mt-1"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
            <Input
              id="password"
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="rounded-none mt-1"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-submit"
            disabled={loading}
            className="w-full rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-11 font-medium"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
